import {
  isCleanIndependentSuccess,
  type MultiplicationAttempt,
} from "./attempt.ts";

export interface FluencyPolicy {
  readonly version: string;
  readonly minimumSamples: number;
  readonly multiplier: number;
  readonly toleranceMs: number;
}

export const DEFAULT_FLUENCY_POLICY: FluencyPolicy = {
  version: "personal-table-fluency-v1",
  minimumSamples: 5,
  multiplier: 1.5,
  toleranceMs: 400,
};

export interface FluencyBaseline {
  readonly sampleCount: number;
  readonly medianThinkingTimeMs: number | null;
  readonly fluentUpperBoundMs: number | null;
  readonly policyVersion: string;
}

export type AttemptFluency = "FLUENT" | "SLOW" | "UNASSESSED" | "INVALID";

function median(values: readonly number[]): number {
  const ordered = [...values].sort((a, b) => a - b);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 === 0
    ? (ordered[middle - 1] + ordered[middle]) / 2
    : ordered[middle];
}

export function buildFluencyBaseline(
  attempts: readonly MultiplicationAttempt[],
  policy: FluencyPolicy = DEFAULT_FLUENCY_POLICY,
): FluencyBaseline {
  const samples = attempts
    .filter(isCleanIndependentSuccess)
    .map((attempt) => attempt.thinkingTimeMs)
    .filter((time): time is number => time !== null && Number.isFinite(time));

  if (samples.length < policy.minimumSamples) {
    return {
      sampleCount: samples.length,
      medianThinkingTimeMs: null,
      fluentUpperBoundMs: null,
      policyVersion: policy.version,
    };
  }

  const medianThinkingTimeMs = median(samples);
  return {
    sampleCount: samples.length,
    medianThinkingTimeMs,
    fluentUpperBoundMs: medianThinkingTimeMs * policy.multiplier + policy.toleranceMs,
    policyVersion: policy.version,
  };
}

export function classifyAttemptFluency(
  attempt: MultiplicationAttempt,
  baseline: FluencyBaseline,
): AttemptFluency {
  if (!isCleanIndependentSuccess(attempt) || attempt.thinkingTimeMs === null) return "INVALID";
  if (baseline.fluentUpperBoundMs === null) return "UNASSESSED";
  return attempt.thinkingTimeMs <= baseline.fluentUpperBoundMs ? "FLUENT" : "SLOW";
}
